<template>
  <teleport to="body">
    <transition name="confirm" appear>
      <div
        v-if="show"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        @click.self="handleCancel"
      >
        <!-- Backdrop -->
        <transition name="backdrop" appear>
          <div 
            v-if="show"
            class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            @click="handleCancel"
          ></div>
        </transition>

        <!-- Confirm Dialog -->
        <transition name="dialog" appear>
          <div 
            v-if="show"
            class="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform"
            @click.stop
          >
            <!-- Header with Icon -->
            <div class="p-6 text-center">
              <!-- Icon Background Circle -->
              <div
                class="mx-auto flex items-center justify-center w-16 h-16 rounded-full mb-4"
                :class="iconBgClass"
              >
                <!-- Success: Check Circle -->
                <svg v-if="type === 'success'" class="w-8 h-8" :class="iconClass" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                </svg>
                <!-- Error: X Circle -->
                <svg v-else-if="type === 'error'" class="w-8 h-8" :class="iconClass" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                <!-- Warning: Exclamation Triangle -->
                <svg v-else-if="type === 'warning'" class="w-8 h-8" :class="iconClass" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a1 1 0 011 1v3a1 1 0 11-2 0V7a1 1 0 011-1zm0 8a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
                <!-- Info: Information Circle -->
                <svg v-else-if="type === 'info'" class="w-8 h-8" :class="iconClass" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <!-- Question: Question Mark Circle -->
                <svg v-else class="w-8 h-8" :class="iconClass" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a1.5 1.5 0 00-1.06 2.56l.3.3a1 1 0 010 1.41l-.3.3A1 1 0 009 11h2a1 1 0 00.7-.29l.3-.3a3.5 3.5 0 00-2-5.91zM10 15a1 1 0 100-2 1 1 0 000 2z" clip-rule="evenodd" />
                </svg>
              </div>
              
              <!-- Title -->
              <h3 class="text-xl font-semibold text-gray-900 mb-2">
                {{ title }}
              </h3>
              
              <!-- Message -->
              <p class="text-gray-600 text-base leading-relaxed" v-if="message">
                {{ message }}
              </p>
              
              <!-- HTML Message (for rich content) -->
              <div 
                v-else-if="html" 
                class="text-gray-600 text-base leading-relaxed"
                v-html="html"
              ></div>
            </div>

            <!-- Actions -->
            <div class="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:justify-center space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
              <button
                v-if="cancelText"
                ref="cancelButton"
                type="button"
                @click="handleCancel"
                :disabled="processing"
                class="cancel-button"
              >
                {{ cancelText }}
              </button>
              
              <button
                ref="confirmButton"
                type="button"
                @click="handleConfirm"
                :disabled="processing"
                class="confirm-button"
                :class="confirmButtonClass"
              >
                <svg v-if="processing" class="loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="button-text">{{ processing ? processingText : confirmText }}</span>
              </button>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, computed, nextTick, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'warning',
    validator: (value) => ['success', 'error', 'warning', 'info', 'question'].includes(value)
  },
  title: {
    type: String,
    default: 'ยืนยันการดำเนินการ'
  },
  message: {
    type: String,
    default: ''
  },
  html: {
    type: String,
    default: ''
  },
  confirmText: {
    type: String,
    default: 'ยืนยัน'
  },
  cancelText: {
    type: String,
    default: 'ยกเลิก'
  },
  processingText: {
    type: String,
    default: 'กำลังดำเนินการ...'
  },
  processing: {
    type: Boolean,
    default: false
  },
  focusCancel: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['confirm', 'cancel', 'close'])

const cancelButton = ref(null)
const confirmButton = ref(null)

// Computed properties for styling
const iconBgClass = computed(() => {
  const classes = {
    success: 'bg-green-100',
    error: 'bg-red-100',
    warning: 'bg-yellow-100',
    info: 'bg-blue-100',
    question: 'bg-purple-100'
  }
  return classes[props.type] || 'bg-yellow-100'
})

const iconClass = computed(() => {
  const classes = {
    success: 'text-green-600',
    error: 'text-red-600',
    warning: 'text-yellow-600',
    info: 'text-blue-600',
    question: 'text-purple-600'
  }
  return classes[props.type] || 'text-yellow-600'
})

const confirmButtonClass = computed(() => {
  const classes = {
    success: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
    error: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
    info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
    question: 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500'
  }
  return classes[props.type] || 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
})

// Methods
const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  if (!props.processing) {
    emit('cancel')
    emit('close')
  }
}

// Focus management
watch(() => props.show, async (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden'
    
    await nextTick()
    
    if (props.focusCancel && cancelButton.value) {
      cancelButton.value.focus()
    } else if (confirmButton.value) {
      confirmButton.value.focus()
    }
  } else {
    document.body.style.overflow = ''
  }
})

// Keyboard handling
const handleKeydown = (event) => {
  if (!props.show) return
  
  if (event.key === 'Escape' && !props.processing) {
    handleCancel()
  }
}

// Add event listener for keyboard
if (typeof window !== 'undefined') {
  window.addEventListener('keydown', handleKeydown)
}
</script>


<style scoped>
/* Button Styles */
.cancel-button {
  width: 100%;
  padding: 0.75rem 1.5rem;
  border: 1px solid #d1d5db;
  border-radius: 0.75rem;
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
  background-color: white;
  transition: all 0.2s ease;
  cursor: pointer;
}

.cancel-button:hover:not(:disabled) {
  background-color: #f9fafb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.cancel-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.confirm-button {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  font-size: 1rem;
  font-weight: 500;
  color: white;
  border: none;
  transition: all 0.2s ease;
  cursor: pointer;
}

.confirm-button:hover:not(:disabled) {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px) scale(1.02);
}

.confirm-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  width: 1.25rem;
  height: 1.25rem;
  margin-right: 0.75rem;
  color: white;
  animation: spin 1s linear infinite;
}

.button-text {
  font-weight: 500;
  font-size: 1rem;
}

/* Media Query for larger screens */
@media (min-width: 640px) {
  .cancel-button, .confirm-button {
    width: auto;
    min-width: 120px;
  }
}

/* Loading Animation */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
/* Transitions */
.confirm-enter-active, .confirm-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.confirm-enter-from, .confirm-leave-to {
  opacity: 0;
}

.backdrop-enter-active, .backdrop-leave-active {
  transition: all 0.3s ease;
}

.backdrop-enter-from, .backdrop-leave-to {
  opacity: 0;
}

.dialog-enter-active, .dialog-leave-active {
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.dialog-enter-from {
  opacity: 0;
  transform: scale(0.8) translateY(-50px);
}

.dialog-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(30px);
}

/* Removed duplicate button styles - moved to top */

/* Dialog animation */
.relative {
  animation: slideInUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes slideInUp {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-50px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Icon animation */
.w-16.h-16 {
  animation: iconBounce 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.2s both;
}

@keyframes iconBounce {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Responsive design */
@media (max-width: 640px) {
  .max-w-md {
    max-width: calc(100vw - 2rem);
  }
  
  .px-6 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .text-xl {
    font-size: 1.125rem;
  }
  
  .text-base {
    font-size: 0.875rem;
  }
}
</style>